import * as vp from 'vopay';

import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import { Balances } from './types';

const { Box, SubTitle, Icon, Table, Badge } = vp;

type KeyValuePair = {
  display: string;
  value: string | React.JSX.Element;
  showField?: boolean;
};

type TablePropsHandlers = {
  account: () => vp.TableProps;
  contact: () => vp.TableProps;
  address: () => vp.TableProps;
};

type DetailsProps = {
  details: vp.AccountDetailsMap;
  agreementDetails: vp.AgreementDetailsResponse;
  onClick?: () => void;
};

type FC = FunctionComponent<DetailsProps>;

const Details: FC = ({ details, agreementDetails, onClick }: DetailsProps) => {
  const { accountId } = useParams<{ accountId: string }>();

  const query = new URLSearchParams(useLocation().search);
  const balance: Balances = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as vp.SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  const { response } = usePartnerLogin();
  const isInsideSales = response.isInsideSales || false;
  const {
    response: { partnerType },
  } = usePartnerLogin();

  const accountDetails = details[accountId];
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (agreementDetails.AgreementStatus !== '' && agreementDetails.AgreementStatus !== undefined) {
      setStatus(agreementDetails.AgreementStatus);
    }
  }, [agreementDetails]);

  const getTableProps = useCallback(
    (handler: keyof TablePropsHandlers): vp.TableProps => {
      const handlers: TablePropsHandlers = {
        account: () => {
          const [accountDetailPairs, setAccountDetailPairs] = useState<KeyValuePair[]>([
            {
              display: 'Account Name',
              value: accountDetails.accountName,
              showField: true,
            },
            {
              display: 'Account ID',
              value: accountDetails.accountId,
              showField: true,
            },
            {
              display: 'Status',
              value: (
                <Badge
                  label={balance.isActive ? vp.AccountStatus.Active : balance.accountStatus}
                  color={balance.isActive ? 'positive' : vp.GetColorStatus(balance.accountStatus || '')}
                  hasIcon
                />
              ),
              showField: true,
            },
            {
              display: 'Agreement Status',
              value:
                status === 'Agreement Not Generated' ? (
                  <div style={{ display: 'flex', gap: 10, height: 10, alignItems: 'center' }}>
                    {status.replace('Agreement ', '')}
                    <div role="button" onClick={onClick} style={{ cursor: 'pointer' }} onKeyDown={onClick} tabIndex={0}>
                      <Icon icon="error" color="error" width="20px" position="right" />
                    </div>
                  </div>
                ) : (
                  <div>{status.replace('Agreement ', '')}</div>
                ),
              showField: false,
            },
          ]);

          useEffect(() => {
            if (isInsideSales) {
              setAccountDetailPairs([
                {
                  display: 'Account Name',
                  value: accountDetails.accountName,
                  showField: true,
                },
                {
                  display: 'Account ID',
                  value: accountDetails.accountId,
                  showField: true,
                },
                {
                  display: 'Status',
                  value: (
                    <Badge
                      label={balance.isActive ? vp.AccountStatus.Active : balance.accountStatus}
                      color={balance.isActive ? 'positive' : vp.GetColorStatus(balance.accountStatus || '')}
                      hasIcon
                    />
                  ),
                  showField: true,
                },
                {
                  display: 'Agreement Status',
                  value:
                    status === 'Agreement Not Generated' ? (
                      <div style={{ display: 'flex', gap: 10, height: 10, alignItems: 'center' }}>
                        {status.replace('Agreement ', '')}
                        <div
                          role="button"
                          onClick={onClick}
                          style={{ cursor: 'pointer' }}
                          onKeyDown={onClick}
                          tabIndex={0}
                        >
                          <Icon icon="error" color="error" width="20px" position="right" />
                        </div>
                      </div>
                    ) : (
                      <div>{status.replace('Agreement ', '')}</div>
                    ),
                  showField: false,
                },
              ]);
            } else {
              setAccountDetailPairs(Object.values(accountDetailPairs).filter((data) => data.showField));
            }
          }, [status, partnerType]);

          const accountInfoProps: vp.TableProps = {
            name: 'account-details',
            variant: 'pair',
            columns: {
              detailKey: { header: 'Account Details' },
              detailValue: { header: '' },
            },
            rows: accountDetailPairs.map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
          };

          return accountInfoProps;
        },
        contact: () => {
          const contactInfoPairs: KeyValuePair[] = [
            { display: 'Email', value: accountDetails.contact.email, showField: true },
            { display: 'Phone', value: accountDetails.contact.phone, showField: !isInsideSales },
            { display: 'Fax', value: accountDetails.contact.fax, showField: !isInsideSales },
          ];

          const contactInfoProps: vp.TableProps = {
            name: 'account-contact',
            variant: 'pair',
            columns: {
              detailKey: { header: 'Contact' },
              detailValue: { header: '' },
            },
            rows: contactInfoPairs
              .filter((data) => data.showField)
              .map(({ display, value }) => ({
                detailKey: display,
                detailValue: value,
              })),
          };

          return contactInfoProps;
        },
        address: () => {
          const addressPairs: KeyValuePair[] = [
            { display: 'Street Address', value: accountDetails.address.addressLine1, showField: !isInsideSales },
            { display: 'Unit', value: accountDetails.address.addressLine2, showField: !isInsideSales },
            { display: 'City', value: accountDetails.address.city, showField: !isInsideSales },
            { display: 'Province', value: accountDetails.address.province, showField: !isInsideSales },
            { display: 'Postal Code', value: accountDetails.address.postalCode, showField: !isInsideSales },
            { display: 'Country', value: accountDetails.address.country, showField: !isInsideSales },
          ];

          const addressInfoProps: vp.TableProps = {
            name: 'account-address',
            variant: 'pair',
            columns: {
              detailKey: { header: 'Address' },
              detailValue: { header: '' },
            },
            rows: addressPairs
              .filter((data) => data.showField)
              .map(({ display, value }) => ({
                detailKey: display,
                detailValue: value,
              })),
          };

          return addressInfoProps;
        },
      };

      return handlers[handler]();
    },
    [status],
  );

  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle="Account Details" />
      </Box>
      <Table {...getTableProps('account')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle="Contact Info" />
      </Box>
      <Table {...getTableProps('contact')} />
      {!isInsideSales && (
        <div>
          <Box mt="lg" mb="md">
            <SubTitle subTitle="Address" />
          </Box>
          <Table {...getTableProps('address')} />
        </div>
      )}
    </div>
  );
};

export default Details;
